import angular from 'angular';
import _ from 'lodash-es';
import $ from 'jquery';
import APIV5 from '~/models/api';
import jsPlumb from 'jsplumb';
import Defaults from '~/scripts/constants/defaults';
import Designer from '~/scripts/services/model/designer';
import Node from '~/scripts/services/model/node';
import Schema from '~/scripts/services/model/schema';
import SchemaComment from '~/scripts/services/model/schema-comment';
import SchemaLink from '~/scripts/services/model/schema-link';
import SchemaNode, { defaultDroit } from '~/scripts/services/model/schema-node';
import { DEFINED_BY, ELEMENT_OF, IS_A } from '~/scripts/constants/common-kids';
import templateModalClassDesignerGenerate from '~/pages/admin/class-designer/modal/modal-class-designer-generate.html';
import { Confirm } from '~/scripts/services/modal/modal';
import LinkStyle from './link-style';
import templateModalDroit from '../../../components/droit/modal/modal-droit.html';
import ModalDroitController from '../../../components/droit/modal/modal-droit.controller';
import NodeDefinitionClassDico from '~/scripts/services/model/node-definition-class-dico';
import NodeDefinitionClassCommon from '~/scripts/services/model/node-definition-class-common';

export const GRID = 25;

let InsertFlag = false;

// noinspection JSUnusedGlobalSymbols
const schemaSourceParameters = {
	filter:           '.node-connect',
	anchor:           'Continuous',
	connector:        [
		new LinkStyle().type,
	],
	maxConnections:   1000,
	onMaxConnections: (/** @type {any} */ info) => {
		// eslint-disable-next-line no-alert
		window.alert(`Maximum connections (${info.maxConnections}) reached`);
	},
};

const schemaTargetParameters = {
	allowLoopback: true,
	anchor:        'Continuous',
	dropOptions:   {
		hoverClass: 'dragHover',
	},
	isTarget:      true,
};

/**
 * @typedef DesignerPanelOptions
 * @property {boolean} create
 * @property {boolean} [isaNodeCreate]
 * @property {boolean} [autoLinks]
 * @property {boolean} [autoNodes]
 * @property {'info' | 'droit'} tab
 */

/** Class ClassDesigner */
export default class ClassDesigner extends Designer {
	#ignoreEventCreateLink;

	/** @type {?number[]} */
	#initialPosition;

	/** @type {?string} */
	#lastSelectedElement;

	/* @ngInject */
	/**
	 * Instantiates an Class Designer
	 *
	 * @param {angular.auto.IInjectorService} $injector
	 */
	constructor($injector) {
		super($injector);

		this.$filter = $injector.get('$filter');
		this.$window = $injector.get('$window');

		this.#ignoreEventCreateLink = false;
		this.#initialPosition = null;
		this.#lastSelectedElement = null;

		this.jspOptions.DuplicateConnectionsAllowed = true;

		/** @type {?number} */
		this.panelWidth = null;

		/** @type {import('~/scripts/services/model/schema').default} */
		this.schema = new Schema($injector);

		/**
		 * @type {{
		 *   modified: boolean,
		 *   node: SchemaNode | SchemaLink | SchemaComment,
		 *   options: ?DesignerPanelOptions
		 * }[]}
		 */
		this.selected = [];
	}

	/**
	 * Adds isa links
	 *
	 * @param {SchemaNode} schemaNode Instance of SchemaNode
	 */
	addLinkIsa(schemaNode) {
		if (schemaNode.node.objtype === 'OBJECT' && this.schema) {
			const temporaryLink = new SchemaLink(this.$injector);
			temporaryLink.type = IS_A;
			temporaryLink.style.updateStyleFromRelation(temporaryLink.type);
			this.#ignoreEventCreateLink = true;
			for (const schemaNode2 of this.schema.schemaNodes) {
				if (schemaNode2.node.kid === schemaNode.node.isa.objid) {
					const connection = this.jsp?.connect({
						source:    `kn-node-${this.id}-${schemaNode.id}`,
						target:    `kn-node-${this.id}-${schemaNode2.id}`,
						connector: 'Straight',
						cssClass:  temporaryLink.style.getCSS(),
						// @ts-ignore
						anchors:   ['Continuous'],
					});
					if (connection) {
						// @ts-ignore
						connection.getOverlay('label').setLabel(temporaryLink.html());
						// @ts-ignore
						$(connection.getOverlay('label').canvas).addClass('link-readonly');
					}
				}
			}
			this.#ignoreEventCreateLink = false;
		}
	}

	/**
	 * @param {SchemaNode} schemaNode
	 */
	async autoAddLinks(schemaNode) {
		this.#ignoreEventCreateLink = true;

		for (const snode of this.schema?.schemaNodes || []) {
			for (const link of snode.node.link) {
				if (link.objnum2_kid === schemaNode.node.kid && snode.node !== schemaNode.node) {
					/** @type {any} */
					const linker = {
						kid:  '0',
						name: undefined,
					};

					if (link.linker_kid !== '0') {
						linker.kid = link.linker_kid;
						if (link.linker_info) {
							linker.name = link.linker_info.name;
						}
						else {
							const nodeLinker = await this.NodeService.load(
								linker.kid,
								undefined,
								undefined,
								['object'],
							);
							linker.name = nodeLinker?.getName();
						}
					}

					/** @type {any} */
					const newLink = {
						kid:      link.kid,
						type:     link.relation_kid,
						readOnly: true,
						data:     link.data,
						linker,
					};
					newLink.from = {
						id:   snode.id,
						kid:  snode.node.kid,
						card: link.card_from === Defaults.KCARDN ? 'N' : link.card_from,
					};
					newLink.to = {
						id:   schemaNode.id,
						kid:  schemaNode.node.kid,
						card: link.card_to === Defaults.KCARDN ? 'N' : link.card_to,
					};
					const oLink = await this.schema?.addSchemaLink(newLink);
					if (oLink) {
						const c = this.jsp?.connect({
							source:    `kn-node-${this.id}-${newLink.from.id}`,
							target:    `kn-node-${this.id}-${newLink.to.id}`,
							connector: oLink?.style.type,
							cssClass:  oLink?.style.getCSS(),
							// @ts-ignore
							anchors: ['Continuous'],
						});
						if (c) {
							// @ts-ignore
							c.scope = oLink.kid;
							// @ts-ignore
							c.getOverlay('label').setLabel(oLink.html());
							// @ts-ignore
							$(c.getOverlay('label').canvas).addClass('link-readonly');
							oLink.setConnection(c);
						}
					}
				}
			}
		}

		for (const link of schemaNode.node.link) {
			if (
				link.objnum2_kid && schemaNode.node.kid !== link.objnum2_kid
				&& this.schema?.getNodeIdFromKid(link.objnum2_kid)
			) {
				/** @type {any} */
				const linker = {
					kid:  '0',
					name: undefined,
				};

				if (link.linker_kid !== '0') {
					linker.kid = link.linker_kid;
					if (link.linker_info) {
						linker.name = link.linker_info.name;
					}
					else {
						const nodeLinker = await this.NodeService.load(
							linker.kid,
							undefined,
							undefined,
							['object'],
						);
						linker.name = nodeLinker?.getName();
					}
				}

				/** @type {any} */
				const newLink = {
					kid:      link.kid,
					type:     link.relation_kid,
					readOnly: true,
					data:     link.data,
					linker,
				};
				if (link.flow === 'from') {
					newLink.from = {
						id:  schemaNode.id,
						kid: schemaNode.node.kid,
						// @ts-ignore
						card: Number.parseInt(link.card_from, 10) === Defaults.KCARDN ? 'N' : link.card_from,
					};
					newLink.to = {
						id:   this.schema.getNodeIdFromKid(link.objnum2_kid),
						kid:  link.objnum2_kid,
						// @ts-ignore
						card: Number.parseInt(link.card_to, 10) === Defaults.KCARDN ? 'N' : link.card_to,
					};

					const oLink = await this.schema?.addSchemaLink(newLink);
					const c = this.jsp?.connect({
						source:    `kn-node-${this.id}-${newLink.from.id}`,
						target:    `kn-node-${this.id}-${newLink.to.id}`,
						connector: oLink.style.type,
						cssClass:  oLink.style.getCSS(),
						// @ts-ignore
						anchors:   ['Continuous'],
					});
					if (c && oLink) {
						// @ts-ignore
						c.scope = oLink.kid;
						// @ts-ignore
						c.getOverlay('label').setLabel(oLink.html());
						// @ts-ignore
						$(c.getOverlay('label').canvas).addClass('link-readonly');
						oLink.setConnection(c);
					}
				}
			}
		}
		this.#ignoreEventCreateLink = false;
	}

	/**
	 * @param {SchemaNode} schemaNode
	 * @returns {import('angular').IPromise<any>}
	 */
	autoAddNodes(schemaNode) {
		let deltaY = 0;

		return this.$q.when(Promise.all(schemaNode.node.link.map(async slink => {
			if ((slink.relation_kid === DEFINED_BY || slink.relation_kid === ELEMENT_OF) && slink.objnum2_kid) {
				// eslint-disable-next-line no-loop-func,promise/no-nesting
				const sLinkNode = await this.NodeService.load(
					slink.objnum2_kid,
					undefined,
					undefined,
					['object'],
				);
				/** @type {SchemaNode} */
				if (sLinkNode?.constructor === Node) {
					const schemaLinkNode = new SchemaNode(sLinkNode);
					schemaLinkNode.existK = true;
					schemaLinkNode.id = this.User.getRef();
					schemaLinkNode.node.objtype = sLinkNode.objtype;
					schemaLinkNode.shape = sLinkNode.objtype === 'CLASS' ? 'RECTANGLE' : 'ELLIPSIS';
					schemaLinkNode.x = (schemaNode?.x || 0) + (5 * GRID);
					schemaLinkNode.y = (schemaNode?.y || 0) + deltaY;
					schemaLinkNode.show();

					deltaY += (GRID * 3);
					this.schema?.addSchemaNode(schemaLinkNode);
				}
			}

			return this.$q.resolve(true);
		})));
	}

	/**
	 * @param {any} [options]
	 * @returns {Promise<boolean>}
	 */
	async checkSelected(options = null) {
		let done = true;
		if (this.selected.length > 0) {
			if (this.selected[0].node instanceof SchemaLink) {
				const oldConnection = this.selected[0].node.getConnection();
				try {
					if (this.jsp && oldConnection?.getOverlay('label')) {
						// @ts-ignore
						$(oldConnection.getOverlay('label').canvas).removeClass('selected');
					}
				}
				catch (/** @type {any} */ error) {
					console.error(error);
				}
			}

			if (this.selected[0].modified) {
				done = await this.nodeConfirmChanges();
			}
			else if (this.selected[0].node instanceof SchemaNode && this.selected[0].options?.create) {
				await this.nodeDelete(this.selected[0].node, false);
			}
			else if (!options?.multiple) {
				this.unselectAll();
			}
		}
		return done;
	}

	/**
	 * Creates a node from mouse event
	 *
	 * @param {MouseEvent} event Mouse event
	 * @returns {Promise<any>} Promise result
	 */
	async commentCreateFromEvent(event) {
		const schemaComment = this.schema?.addSchemaComment({
			id: this.User.getRef(),
			x: Math.round((event.offsetX / this.scale) / GRID) * GRID,
			y: Math.round((event.offsetY / this.scale) / GRID) * GRID,
		});
		// await this.$timeout(() => this.$rootScope.$apply());

		await this.$timeout(async () => {
			/** @type {any} */
			const $element = $(`#kn-comment-${this.id}-${schemaComment.id}`);
			this.jsp?.draggable($element, {
				containment:   '.tab-pane.active .schema-editor',
				stop:          (/** @type {any} */ decOptions) => this.nodeMoveStop(decOptions),
				disabled:      !this.edit,
				snapThreshold: 13,
				grid:          [
					GRID,
					GRID,
				],
			});
			this.jsp?.setDraggable($element, this.edit);
		});

		return this.commentEdit(schemaComment, { muliple: false });
	}

	/**
	 * Deletes a comment
	 *
	 * @param {SchemaComment} schemaComment Instance of SchemaComment
	 * @param {boolean} [showConfirmation]
	 * @returns {Promise<boolean>} Return result
	 */
	async commentDelete(schemaComment, showConfirmation = true) {
		let answer;

		if (showConfirmation) {
			const modal = new Confirm(this.$injector);
			answer = await modal.open({
				title:  this.$translate.instant('designer.delete-node'),
				body:   this.$translate.instant('designer.confirm-delete-comment'),
				accept: {
					label: this.$translate.instant('common.delete'),
					type:  'danger',
				},
			});
		}
		else {
			answer = true;
		}

		if (answer && this.schema) {
			if ($(`#kn-comment-${this.id}-${schemaComment.id}`).length > 0) {
				this.jsp?.remove(`kn-comment-${this.id}-${schemaComment.id}`);
			}
			_.remove(this.schema.comments, c => c.id === schemaComment.id);
			this.modified = true;

			if (this.selected.length === 1) {
				this.unselectAll();
			}
		}

		return true;
	}

	/**
	 * Display the node (for creation/edition)
	 *
	 * @param {SchemaComment} schemaComment Instance of SchemaComment to edit
	 * @param {any} options
	 * @returns {Promise<any>} Promise result
	 */
	async commentEdit(schemaComment, options) {
		this.$timeout(() => this.select(
			schemaComment,
			{ type: 'comment', ...options },
		));
	}

	/**
	 * @param {SchemaComment} schemaComment
	 */
	async commentUpdate(schemaComment) {
		if (this.selected.length === 0 || !this.selected[0].node) {
			return;
		}

		const currentSchemaComment = this.selected[0].node;
		if (currentSchemaComment instanceof SchemaComment) {
			currentSchemaComment.comment = schemaComment.comment;
			this.$timeout(() => {
				if (this.selected.length > 0) {
					this.unselectAll();
				}
			});

			this.modified = true;
		}
	}

	/**
	 * @param {import('@knitiv/api-client-javascript').KNode} schemaId
	 * @returns {Promise<ClassDesigner>}
	 */
	async createTab(schemaId) {
		if (this.schema) {
			this.schema.kid = schemaId;
		}
		this.create = true;
		this.edit = true;
		this.id = schemaId.split(';')[1];

		return this;
	}

	/**
	 * Displays the content of the tab
	 */
	display() {
		if (!this.jsp) {
			return;
		}
		const $schemaContainer = $('.tab-pane:visible').find('.schema-content');
		const $schemaEditor = $schemaContainer.find('.schema-editor');

		// ---- d&d schema ----
		/** @type {boolean} */
		let clicking = false;

		/** @type {boolean} */
		let moving = false;

		/** @type {number} */
		let previousX = 0;

		/** @type {number} */
		let previousY = 0;

		// @ts-ignore
		this.jsp.Defaults.Container = $schemaEditor;
		this.schemaOffset = $schemaEditor.offset();

		$schemaEditor.bind('click', event => {
			// @ts-ignore
			if (!moving && $(event.target).hasClass('schema-editor')) {
				this.unselectAll();
			}
			clicking = false;
			moving = false;
		});

		$schemaEditor.bind('mousedown', event => {
			// @ts-ignore
			if ($(event.target).hasClass('schema-editor')) {
				event.preventDefault();
				previousX = event.clientX;
				previousY = event.clientY;
				clicking = true;
			}
		});

		$(document).bind('mouseup', event => {
			// Deselect previously selected node
			// @ts-ignore
			if (!moving && $(event.target).hasClass('schema-editor')) {
				this.$timeout(() => this.checkSelected());
			}
			clicking = false;
			moving = false;
		});

		$schemaEditor.bind('mousemove', event => {
			if ($schemaContainer && clicking) {
				moving = true;
				event.preventDefault();
				$schemaContainer.scrollLeft(($schemaContainer.scrollLeft() || 0) + (previousX - event.clientX));
				$schemaContainer.scrollTop(($schemaContainer.scrollTop() || 0) + (previousY - event.clientY));
				previousX = event.clientX;
				previousY = event.clientY;
			}
		});

		$schemaEditor.bind('mouseleave', () => {
			clicking = false;
		});

		// ---- zoom schema ----
		$(window).bind('keydown', event => {
			if (event.keyCode === 107 && (event.ctrlKey || event.metaKey)) {
				event.preventDefault();
				this.zoomIn();
			}
			else if (event.keyCode === 109 && (event.ctrlKey || event.metaKey)) {
				event.preventDefault();
				this.zoomOut();
			}
		});
		$schemaEditor.bind('mousewheel DOMMouseScroll', event => {
			// @ts-ignore
			if ((event.ctrlKey || event.metaKey) && event.originalEvent.deltaY < 0) {
				event.preventDefault();
				this.zoomIn();
			}
			// @ts-ignore
			else if ((event.ctrlKey || event.metaKey) && event.originalEvent.deltaY > 0) {
				event.preventDefault();
				this.zoomOut();
			}
		});

		// event sur click connection (affichage du modal Link)
		// eslint-disable-next-line unicorn/prefer-prototype-methods
		// @ts-ignore
		this.jsp?.bind('click', async (/** @type {jsPlumb.Connection} */ connection, /** @type {MouseEvent} */ event) => {
			event.preventDefault();
			const schemaLink = this.schema?.links.find(
				(/** @type {SchemaLink} */ link) => link.getConnection()?.id === connection.id,
			);
			if (schemaLink) {
				await this.linkEdit(schemaLink, { create: false });
			}
		});

		InsertFlag = true;

		this.jsp?.bind('connectionDrag', () => {
			this.connecting = true;
		});

		this.jsp?.bind('connectionDragStop', () => {
			this.connecting = false;
		});

		// event sur connection entre 2 états (ajout/modif d'une action)
		// eslint-disable-next-line unicorn/prefer-prototype-methods
		this.jsp?.bind('connection', async info => {
			if (!InsertFlag && !this.#ignoreEventCreateLink) {
				const schemaLink = this.schema?.links.find(
					(/** @type {SchemaLink} */ link) => link.getConnection()?.id === info.connection.id,
				);

				// @ts-ignore
				if (info.connection.suspendedElement) {
					if (schemaLink) {
						this.linkUpdate(schemaLink);
					}
				}
				else {
					await this.linkAdd(info.connection);
				}
			}

			this.$timeout(() => {
				const schemaLink = this.schema?.links.find(
					(/** @type {SchemaLink} */ link) => link.getConnection()?.id === info.connection.id,
				);

				// @ts-ignore
				$(info.connection.getOverlay('label').getElement()).contextmenu(this.linkContextMenuOptions(schemaLink));
			});
		});

		// eslint-disable-next-line unicorn/prefer-prototype-methods
		this.jsp?.bind('beforeDetach', () => false);

		// suspend drawing and initialise.
		const $nodeList = $schemaEditor.find('.node');

		// mettre à jour la position x/y en cas de déplacement
		this.jsp?.draggable($nodeList, {
			// @ts-ignore
			containment:   $schemaEditor,
			stop:          (/** @type {any} */ decOptions) => this.nodeMoveStop(decOptions),
			disabled:      !this.edit,
			snapThreshold: 13,
			grid:          [
				GRID,
				GRID,
			],
		});
		this.jsp?.setDraggable($nodeList, this.edit);

		if (this.schema && $nodeList.length > 0) {
			this.jsp?.batch(() => {
				const linksToDelete = [];
				let connection;
				let source;
				let target;

				// @ts-ignore
				this.jsp?.makeSource($nodeList, schemaSourceParameters);
				// @ts-ignore
				this.jsp?.makeTarget($nodeList, schemaTargetParameters);

				if (this.schema) {
					for (const [k, link] of this.schema.links.entries()) {
						if (link.from && link.to) {
							const idFrom = link.from.id ?? (link.from.kid && this.schema.getNodeIdFromKid(link.from.kid));
							const idTo = link.to.id ?? (link.to.kid && this.schema.getNodeIdFromKid(link.to.kid));

							if (idFrom && idTo) {
								source = `kn-node-${this.id}-${
									// @ts-ignore
									'id' in link.from ? link.from.id : this.schema.getNodeIdFromKid(link.from.kid)}`;

								target = `kn-node-${this.id}-${
									// @ts-ignore
									'id' in link.to ? link.to.id : this.schema.getNodeIdFromKid(link.to.kid)}`;

								connection = this.jsp?.connect({
									source,
									target,
									cssClass: link.style.getCSS(),
									// @ts-ignore
									anchors: ['Continuous'],
								});

								if (connection) {
									// @ts-ignore
									connection.setConnector(link.style.type);
									link.setLabel(connection);
									this.schema.links[k].setConnection(connection);
								}
								else {
									linksToDelete.push(k);
								}

								if (linksToDelete.length > 0) {
									let index = this.schema.links.length - 1;
									while (index >= 0) {
										if (linksToDelete.includes(index)) {
											this.schema.links.splice(index, 1);
											this.edit = true;
										}
										index -= 1;
									}
								}
							}
							else {
								console.warn('cannot connect', link.from, 'to', link.to);
							}
						}
					}
				}
			});

			for (const schemaNode of this.schema.schemaNodes) {
				this.addLinkIsa(schemaNode);
			}
		}

		// @ts-ignore
		this.jsp?.fire('jsPlumbSchemaLoaded', this.jsp);
		InsertFlag = false;

		// this.resize();
	}

	/**
	 * Generates the schema to put in prod
	 */
	async generate() {
		if (this.schema?.kid) {
			await this.$uibModal.open({
				ariaLabelledBy:                  'modal-class-designer-generate-title',
				ariaDescribedBy:                 'modal-class-designer-generate-body',
				template:                        templateModalClassDesignerGenerate,
				controller:      /* @ngInject */ (
					/** @type {any} */ $scope,
					/** @type {angular.ui.bootstrap.IModalInstanceService} */ $uibModalInstance,
				) => {
					$scope.generate = $uibModalInstance.close;
					$scope.cancel = () => {
						$uibModalInstance.dismiss('cancel');
					};
				},
				size:                            'sm',
				resolve:                         {
					itemName: () => this.schema?.name,
				},
			}).result;

			const result = await APIV5.instance.classDesignerSetToK(
				{ kid: this.schema.kid },
				{ id: 'class-designer.generate' },
			);

			if (result) {
				this.Notification.info({
					title:   this.$translate.instant('object.successfully-action-message'),
					message: this.$translate.instant('designer.sucess-set-to-k'),
				});
			}
		}
	}

	/**
	 * Import a JSON class designer schema
	 *
	 * @param {File} file File to import
	 */
	import(file) {
		const fr = new FileReader();

		fr.addEventListener('load', async () => {
			let newSchema;
			const flagSetModified = false;

			try {
				newSchema = JSON.parse(fr?.result?.toString() || '');
			}
			catch {
				this.Notification.error({
					title:   this.$translate.instant('error.title'),
					message: this.$translate.instant('error.no-json-file'),
				});
			}

			if (this.schema) {
				// backup existing nodes and links
				const originalSchemaNodes = _.clone(this.schema.schemaNodes);

				const originalSchemaLinks = this.schema.links.map((/** @type {SchemaLink} */ link) => ({
					from:       link.from.kid,
					to:         link.to.kid,
					linker_kid: link.linker.kid,
				}));

				await this.schema.load({ schema: newSchema }, {});

				InsertFlag = true;
				if (this.jsp) {
					this.$timeout(() => {
						this.$rootScope.$apply();

						/** @type {any} */
						const $nodeList = this.setDraggable();

						if ($nodeList.length > 0) {
							this.jsp?.batch(() => {
								this.jsp?.makeSource($nodeList, schemaSourceParameters);
								this.jsp?.makeTarget($nodeList, schemaTargetParameters);
								let connection;
								let source;
								let target;
								for (const [k, link] of this.schema?.links.entries() || []) {
									const addLink = !originalSchemaLinks.some(
										(/** @type {any} */ l2) => l2.from === link.from.kid && l2.to === link.to.kid
											&& l2.linker_kid && link.linker.kid,
									);

									if (addLink && link.from.kid && link.to.kid) {
										source = `kn-node-${this.id}-${'id' in link.from
											// @ts-ignore
											? link.from.id : this.schema?.getNodeIdFromKid(link.from.kid)}`;

										target = `kn-node-${this.id}-${'id' in link.to
											// @ts-ignore
											? link.to.id : this.schema?.getNodeIdFromKid(link.to.kid)}`;

										connection = this.jsp?.connect({
											source,
											target,
											connector: link.style.type,
											cssClass:  link.style.getCSS(),
											// @ts-ignore
											anchors:   ['Continuous'],
										});

										if (connection) {
											link.setLabel(connection);
											this.schema?.links[k].setConnection(connection);
										}
									}
								}
							});

							const newSchemaNodes = [];
							if (originalSchemaNodes && this.schema?.schemaNodes) {
								// eslint-disable-next-line no-unsafe-optional-chaining
								newSchemaNodes.push(...this.schema?.schemaNodes.filter(
									(/** @type {SchemaNode} */ node) => !originalSchemaNodes.some(
										(/** @type {SchemaNode} */ node2) => node2.id === node.id,
									),
								));
							}

							for (const schemaNode of newSchemaNodes) {
								this.addLinkIsa(schemaNode);
							}
						}

						if (flagSetModified) {
							this.modified = true;
						}

						// @ts-ignore
						this.jsp?.fire('jsPlumbSchemaLoaded', this.jsp);
						InsertFlag = false;
					});
				}
			}
		});
		if (file !== undefined) {
			// eslint-disable-next-line unicorn/prefer-blob-reading-methods
			fr.readAsText(file);
		}
	}

	/**
	 * Initializes the jsPlumb instance
	 *
	 * @param {import('jsplumb').jsPlumbInstance
	 *   & import('~/scripts/services/model/designer').jsPlumbExtra} jsp jsPlumb instance
	 */
	init(jsp) {
		this.jsp = jsp;
		this.jspOptions.ConnectionOverlays.push(
			[
				'Label',
				{
					// @ts-ignore
					cssClass: 'link',
					id:       'label',
					label:    new SchemaLink(this.$injector).html(),
				},
			],
		);

		// @ts-ignore
		this.jsp.revalidateEverything = () => {
			// @ts-ignore
			if (this.jsp?.Defaults.Container) {
				// @ts-ignore
				const visibleEndpoints = this.jsp?.Defaults.Container.find('._jsPlumb_ng_endpoint:visible');
				for (const element of visibleEndpoints) {
					// @ts-ignore
					this.jsp?.revalidate($(element).attr('id'));
				}
			}
		};

		this.$timeout(() => {
			const temporaryStyle = new LinkStyle();
			if (this.jsp) {
				// @ts-ignore
				this.jsp.connectorClass = temporaryStyle.getCSS();
			}
			this.display();
		});
	}

	/**
	 * Adds a link
	 *
	 * @param {jsPlumb.Connection} connection jsPlumb connection instance
	 */
	async linkAdd(connection) {
		/** @type {any} */
		let r;

		/** @type {SchemaNode | undefined} */
		let source;

		/** @type {SchemaNode | undefined} */
		let target;

		r = /^kn-node-([\dA-Z]+)-([\dA-Z]+)$/.exec(connection.sourceId);
		if (r && r[1] === this.id) {
			source = this.schema?.getNode(r[2]);
		}

		r = /^kn-node-([\dA-Z]+)-([\dA-Z]+)$/.exec(connection.targetId);
		if (r && r[1] === this.id) {
			target = this.schema?.getNode(r[2]);
		}

		if (this.schema && source && target) {
			const newLink = await this.schema.addSchemaLink({
				kid:      `K_LINK;${this.User.getRefNr()}`,
				readOnly: false,
				from:     {
					id:   source.id,
					kid:  source.node.kid,
					card: Defaults.schema.DEFAULT_CARD_FROM,
				},
				to:       {
					id:   target.id,
					kid:  target.node.kid,
					card: Defaults.schema.DEFAULT_CARD_TO,
				},
			}, true);
			// @ts-ignore
			connection.getOverlay('label').setLabel(newLink.html());
			newLink.setConnection(connection);
			await this.linkEdit(newLink, { create: true });
		}
	}

	/**
	 * Sets the context menu for link
	 *
	 * @param {SchemaLink} schemaLink jsPlumb connection instance
	 * @returns {any} Context menu
	 */
	linkContextMenuOptions(schemaLink) {
		// noinspection JSUnusedGlobalSymbols
		return {
			before: () => schemaLink !== undefined,
			target: '.tab-pane.active .context-menu-link',
			onItem: async (/** @type {any} */ context, /** @type {Event} */ event) => {
				event.preventDefault();
				// @ts-ignore
				if (schemaLink && !$(event.currentTarget).find('a').hasClass('disabled')) {
					// @ts-ignore
					switch ($(event.currentTarget).attr('id')) {
					case 'menuLinkView':
					case 'menuLinkEdit': {
						await this.linkEdit(schemaLink, { create: false });
						break;
					}

					case 'menuLinkDel': {
						await this.linkDelete(schemaLink, false);
						break;
					}

					default: {
						break;
					}
					}
				}
			},
		};
	}

	/**
	 * Delete a link
	 *
	 * @param {SchemaLink} schemaLink jsPlumb connection instance
	 * @param {boolean} force Delete the action without confirmation
	 * @returns {Promise<boolean>}
	 */
	async linkDelete(schemaLink, force = false) {
		const connection = schemaLink.getConnection();

		let pass = true;

		if (!force) {
			pass = false;
			const modal = new Confirm(this.$injector);
			const answer = await modal.open({
				title:  this.$translate.instant('designer.delete-link'),
				body:   this.$filter('sprintf')(this.$translate.instant('designer.confirm-delete-link'), schemaLink.getName()),
				accept: {
					label: this.$translate.instant('common.delete'),
					type:  'danger',
				},
			});

			if (answer) {
				pass = true;
			}
		}

		// @ts-ignore
		if (connection && pass && this.jsp?.deleteConnection(connection, { force: true })) {
			this.schema.removeSchemaLink(schemaLink);
			this.$timeout(() => {
				this.modified = true;
			});
			return true;
		}

		return false;
	}

	/**
	 * Display the link (for creation/edition)
	 *
	 * @param {SchemaLink} schemaLink jsPlumb connection instance
	 * @param {any} options Add link for creation
	 */
	async linkEdit(schemaLink, options) {
		this.$timeout(() => {
			this.select(
				schemaLink,
				{ type: 'link', ...options },
			);
		});
	}

	/**
	 * Update the link
	 *
	 * @param {SchemaLink} schemaLink
	 */
	linkUpdate(schemaLink) {
		if (this.schema && schemaLink?.from.id && schemaLink?.to.id) {
			const relation = this.schema.relations.find((/** @type {any} */ r) => r.kid === schemaLink.type);
			const source = this.schema.getNode(schemaLink.from.id);
			const target = this.schema.getNode(schemaLink.to.id);

			if (source && target) {
				if (relation && schemaLink.from?.id && schemaLink.to?.id) {
					// check for existing CLASS.DICO
					const dicoIndex = source.node.kndef.CLASS.DICO
						&& Object.keys(source.node.kndef.CLASS.DICO).findIndex(
							key => source.node.kndef.CLASS.DICO[key].relation_kid === schemaLink.type
								&& source.node.kndef.CLASS.DICO[key].objnum2_kid === target.node.kid
								&& source.node.kndef.CLASS.DICO[key].linker_kid === schemaLink.linker.kid,
						);

					if (dicoIndex >= 0 && !schemaLink.create) {
						schemaLink.dico_id = Object.keys(source.node.kndef.CLASS.DICO)[dicoIndex];
					}
					else if (relation?.type.length > 0 && relation?.type[0] === 'def') {
						if (!schemaLink.dico_id) {
							// adds CLASS.DICO item
							if (!source.node.kndef.CLASS.DICO) {
								source.node.kndef.CLASS.DICO = {};
							}
							const dicoId = source.node.kndef.add('CLASS', 'DICO', new NodeDefinitionClassDico({
								relation_kid: schemaLink.type,
								objnum2_kid:  target.node.kid,
								objnum2_info: {
									name: target.node.getName(),
								},
								linker_kid:   schemaLink.linker.kid,
							}));

							// stores dico_id
							schemaLink.dico_id = dicoId;

							// adds CLASS.COMMON item
							if (!source.node.kndef.CLASS.COMMON) {
								source.node.kndef.CLASS.COMMON = [];
							}
							source.node.kndef.add('CLASS', 'COMMON', new NodeDefinitionClassCommon({
								type:      'link',
								dico_id:   dicoId,
								card_from: schemaLink.from.card,
								card_to:   schemaLink.to.card,
								lock:      0,
								data:      schemaLink.data,
							}));

							// remove create flag
							schemaLink.create = false;
						}
						else if (schemaLink.dico_id) {
							// updates CLASS.DICO item
							source.node.kndef.CLASS.DICO[schemaLink.dico_id].relation_kid = schemaLink.type;
							source.node.kndef.CLASS.DICO[schemaLink.dico_id].linker_kid = schemaLink.linker?.kid || '0';

							// updates CLASS.COMMON item
							const key = source.node.kndef.CLASS.COMMON
								.findIndex((/** @type {any} */ common) => common.dico_id === schemaLink.dico_id);

							source.node.kndef.update(
								'CLASS',
								'COMMON',
								key,
								new NodeDefinitionClassCommon({
									type:      'link',
									dico_id:   schemaLink.dico_id,
									card_from: schemaLink.from.card,
									card_to:   schemaLink.to.card,
									lock:      0,
									data:      schemaLink.data,
								}),
							);
						}
					}
					else if (relation?.type.length > 0 && relation?.type[0] !== 'def' && schemaLink.dico_id) {
						// deletes CLASS.COMMON item
						_.remove(source.node.kndef.CLASS.COMMON, common => common.dico_id === schemaLink.dico_id);

						// deletes CLASS.DICO item
						delete source.node.kndef.CLASS.DICO[schemaLink.dico_id];
						schemaLink.dico_id = null;
					}
				}

				const index = this.schema.links.findIndex(
					(/** @type {SchemaLink} */ link) => link.kid && schemaLink.kid === link.kid,
				);
				const connection = this.schema.links[index].getConnection();

				let { dico_id: dicoId } = schemaLink;

				// recherche du dico
				if (!dicoId) {
					dicoId = _.findKey(source.node.kndef.CLASS.DICO, dico => dico.objnum2_kid === target.node.kid) || null;
				}

				if (dicoId) {
					// update card from/to
					for (const common of Object.values(source.node.kndef.CLASS.COMMON)) {
						if (common.dico_id === dicoId) {
							schemaLink.from.card = common.card_from;
							// eslint-disable-next-line unicorn/consistent-destructuring
							schemaLink.to.card = common.card_to;
						}
					}
				}

				if (connection) {
					// @ts-ignore
					connection.canvas.classList.remove(...connection.canvas.classList);
					// @ts-ignore
					// connection.getOverlay('label').setLabel(schemaLink.html());
					// @ts-ignore
					// eslint-disable-next-line unicorn/consistent-destructuring
					connection.setConnector(schemaLink.style.type);
					connection.addOverlay([
						'PlainArrow',
						{
							id:       'arrow',
							length:   9,
							location: 1,
						},
					]);
					connection.removeOverlay('label');
					connection.addOverlay([
						'Label',
						{
							cssClass: 'link',
							id:       'label',
							label:    schemaLink.html(),
						},
					]);
					// @ts-ignore
					// eslint-disable-next-line unicorn/consistent-destructuring
					connection.getOverlay('arrow').canvas.className.baseVal = schemaLink.style.getCSS();
					// @ts-ignore
					$(connection.getOverlay('label').getElement()).contextmenu(this.linkContextMenuOptions(schemaLink));
					schemaLink.setConnection(connection);
				}

				if (index >= 0) {
					_.extend(this.schema.links[index], schemaLink);
				}
				this.modified = true;
			}
		}

		if (this.selected.length === 1) {
			this.unselectAll();
		}
	}

	/**
	 * Loads an existing schema
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} schemaId Schema KID
	 * @param {boolean} edit If false then read-only mode
	 * @param {boolean} duplicate Duplicate this schema
	 * @returns {Promise<ClassDesigner | null>} Promise result
	 */
	async load(schemaId, edit, duplicate) {
		/** @type {any} */
		this.loading = true;
		const cd = await APIV5.instance.classDesignerLoad({
			kid: schemaId,
		}, {
			id: 'class-designer.load',
		});
		if (cd) {
			[, this.id] = schemaId.split(';');
			this.schema = new Schema(this.$injector, schemaId);
			await this.schema?.load(cd.result, cd.dico);
			this.edit = edit;
			this.loading = false;
			this.modified = false;

			if (duplicate) {
				this.schema.create = true;
				this.schema.setName(`${this.schema.getName()} (${this.$translate.instant('common.copy')})`);
				this.schema.name = this.schema.getName(true);
				this.schema.kid = `K_NODE;${this.User.getRef()}`;
				[, this.id] = this.schema.kid.split(';');
				this.schema.id = this.id;
				this.modified = true;
			}
			return this;
		}
		this.loading = false;

		this.Notification.warning({
			title:   this.$translate.instant('error.title'),
			message: this.$translate.instant('designer.object-not-supported'),
		});
		return null;
	}

	/**
	 * @param {MouseEvent} event
	 * @param {SchemaNode | SchemaComment} schemaNode
	 */
	async nodeClick(event, schemaNode) {
		// @ts-ignore
		if (!this.connecting && event.button === 0 && $(event.target).closest('.jtk-drag').length === 0) {
			if (schemaNode instanceof SchemaNode) {
				await this.nodeEdit(schemaNode, {
					create: false,
					tab: 'info',
					multiple: this.edit && (event.shiftKey || event.ctrlKey),
				});
			}
			if (schemaNode instanceof SchemaComment) {
				await this.commentEdit(schemaNode, {
					multiple: this.edit && (event.shiftKey || event.ctrlKey),
				});
			}
		}
	}

	/**
	 * @returns {Promise<boolean>}
	 */
	async nodeConfirmChanges() {
		let done = this.selected.length > 0;
		if (this.selected.length === 1) {
			const modal = new Confirm(this.$injector);
			done = await modal.open({
				title:  this.$translate.instant('common.confirmation'),
				body:   this.$sce.trustAsHtml(this.$injector.get('$filter')('sprintf')(
					this.selected[0].node instanceof SchemaNode
						? this.$translate.instant('designer.confirm-save-node')
						: this.$translate.instant('designer.confirm-save-link'),
					this.selected[0].node instanceof SchemaNode || this.selected[0].node instanceof SchemaLink
						? this.selected[0].node.getName()
						: this.$translate.instant('common.comment'),
				)),
				cancel: {
					label: this.$translate.instant('common.cancel'),
				},
				accept: {
					label: this.$translate.instant('common.save'),
				},
			});
			if (done) {
				this.$rootScope.$broadcast('admin-class-designer-submit-node');
			}
		}
		return done;
	}

	/**
	 * Creates a node from mouse event
	 *
	 * @param {MouseEvent} event Mouse event
	 * @param {'CLASS' | 'OBJECT'} objtype Object type
	 * @param {import('@knitiv/api-client-javascript').KNode} isa Node ISA
	 * @param {DesignerPanelOptions} options Options
	 * @returns {Promise<any>} Promise result
	 */
	async nodeCreateFromEvent(event, objtype, isa, options) {
		const id = this.User.getRef();
		const $schemaEditor = $('.tab-pane.active').find('.schema-editor');

		/** @type {SchemaNode} */
		const schemaNode = new SchemaNode(this.NodeService.create(isa, `K_NODE;${id}`));
		schemaNode.id = id;
		schemaNode.node.objtype = objtype;
		schemaNode.shape = objtype === 'CLASS' ? 'RECTANGLE' : 'ELLIPSIS';
		schemaNode.x = Math.round((event.offsetX / this.scale) / GRID) * GRID;
		schemaNode.y = Math.round((event.offsetY / this.scale) / GRID) * GRID;
		schemaNode.node.addDroit(defaultDroit);
		schemaNode.show();

		schemaNode.node.setName(
			objtype === 'CLASS'
				? this.$translate.instant('designer.new-class')
				: this.$translate.instant('designer.new-object'),
		);

		this.schema?.addSchemaNode(schemaNode);
		// await this.$timeout(() => this.$rootScope.$apply());

		await this.$timeout(async () => {
			/** @type {any} */
			const $element = $(`#kn-node-${this.id}-${schemaNode.id}`);
			this.jsp?.draggable($element, {
				// @ts-ignore
				containment:   $schemaEditor,
				stop:          (/** @type {any} */ decOptions) => this.nodeMoveStop(decOptions),
				disabled:      !this.edit,
				snapThreshold: 13,
				grid:          [
					GRID,
					GRID,
				],
			});
			this.jsp?.setDraggable($element, this.edit);
			this.jsp?.makeSource($element, schemaSourceParameters);
			this.jsp?.makeTarget($element, schemaTargetParameters);
		});

		return this.nodeEdit(schemaNode, options);
	}

	/**
	 * Deletes a node
	 *
	 * @param {SchemaNode} schemaNode Instance of SchemaNode
	 * @param {boolean} [showConfirmation=true]
	 * @returns {Promise<boolean>} Return result
	 */
	async nodeDelete(schemaNode, showConfirmation = true) {
		// si le node est une classe, vérifier s'il n'y a pas des objets isa à celle-ci
		const deleteDependencies = schemaNode.node.objtype === 'CLASS' && this.schema?.schemaNodes.some(
			(/** @type {SchemaNode} */schemaNode2) => schemaNode2.node.objtype === 'OBJECT'
				&& schemaNode2.node.isa.objid === schemaNode.node.kid,
		);

		let answer;

		const deleteMultiple = this.selected.some(
			s => !(s.node instanceof SchemaLink) && s.node.id !== schemaNode.id,
		);

		if (showConfirmation) {
			const modal = new Confirm(this.$injector);
			let body = deleteMultiple
				? this.$translate.instant('designer.confirm-delete-nodes')
				: this.$filter('sprintf')(this.$translate.instant('designer.confirm-delete-node'), schemaNode.getName());

			if (deleteDependencies) {
				body += `<br><br>${this.$translate.instant('designer.confirm-delete-node-objects')}`;
			}

			answer = await modal.open({
				title:  this.$translate.instant('designer.delete-node'),
				body,
				accept: {
					label: this.$translate.instant('common.delete'),
					type:  'danger',
				},
			});
		}
		else {
			answer = true;
		}

		if (answer && this.schema) {
			if (deleteDependencies) {
				for (const schemaNode2 of this.schema.schemaNodes) {
					if (schemaNode2.node.objtype === 'OBJECT' && schemaNode2.node.isa.objid === schemaNode.node.kid) {
						this.jsp?.remove(`kn-node-${this.id}-${schemaNode2.id}`);
						schemaNode2.hide();
					}
				}
			}

			// Suppression des liens dépendants de ce nœud
			let indexSchemaLink = this.schema.links.length - 1;
			while (indexSchemaLink >= 0) {
				const schemaLink = this.schema.links[indexSchemaLink];
				if (schemaLink.from.id === schemaNode.id || schemaLink.to.id === schemaNode.id) {
					const connection = schemaLink.getConnection();
					if (connection) {
						// @ts-ignore
						this.jsp?.deleteConnection(connection, { force: true });
					}
					this.schema.removeSchemaLink(schemaLink);
				}
				indexSchemaLink--;
			}

			if ($(`#kn-node-${this.id}-${schemaNode.id}`).length > 0) {
				this.jsp?.remove(`kn-node-${this.id}-${schemaNode.id}`);
			}
			if (schemaNode.node.create) {
				_.remove(this.schema.schemaNodes, schemaNode2 => schemaNode2.id === schemaNode.id);
			}
			else {
				schemaNode.hide();
				this.modified = true;
			}

			if (deleteMultiple) {
				const otherNodesToDelete = this.selected.filter(
					s => !(s.node instanceof SchemaLink) && s.node.id !== schemaNode.id,
				);
				if (otherNodesToDelete) {
					for (const n of otherNodesToDelete) {
						if (!(n.node instanceof SchemaLink)) {
							const type = n.node instanceof SchemaComment ? 'comment' : 'node';
							if ($(`#kn-${type}-${this.id}-${n.node.id}`).length > 0) {
								this.jsp?.remove(`kn-node-${this.id}-${n.node.id}`);
							}
							if (n.node instanceof SchemaNode) {
								if (n.node.node.create) {
									_.remove(
										this.schema.schemaNodes,
										schemaNode2 => n.node instanceof SchemaNode && schemaNode2.id === n.node.id,
									);
								}
								else {
									n.node.hide();
									this.modified = true;
								}
							}
						}
					}
				}
			}

			if (this.selected.length > 0) {
				this.unselectAll();
			}
		}

		return true;
	}

	/**
	 * Display the node (for creation/edition)
	 *
	 * @param {SchemaNode} schemaNode Instance of SchemaNode to edit
	 * @param {any} [options] Options
	 * @returns {Promise<any>} Promise result
	 */
	async nodeEdit(
		schemaNode,
		options = null,
	) {
		this.$timeout(() => this.select(
			schemaNode,
			{ type: 'node', ...options },
		));
	}

	/**
	 * Hides a node
	 *
	 * @param {SchemaNode} schemaNode Instance of SchemaNode
	 * @returns {boolean} Return result
	 */
	nodeHide(schemaNode) {
		// Suppression des liens dépendants de ce nœud
		let indexSchemaLink = this.schema.links.length - 1;
		while (indexSchemaLink >= 0) {
			const schemaLink = this.schema.links[indexSchemaLink];
			if (schemaLink.from.id === schemaNode.id || schemaLink.to.id === schemaNode.id) {
				const connection = schemaLink.getConnection();
				if (connection) {
					// @ts-ignore
					this.jsp?.deleteConnection(connection, { force: true });
				}
				this.schema.removeSchemaLink(schemaLink);
			}
			indexSchemaLink--;
		}

		this.jsp?.remove(`kn-node-${this.id}-${schemaNode.id}`);
		_.remove(this.schema.schemaNodes, schemaNode2 => schemaNode2 === schemaNode);

		this.modified = true;

		return true;
	}

	/**
	 * Callback function after dragging an node element on the drop area
	 *
	 * @param {import('jsplumb').DragEventCallbackOptions} decOptions Handler element
	 */
	nodeMoveStop(decOptions) {
		// @ts-ignore
		const { schemaNode, schemaComment } = $(decOptions.el).scope().$parent;
		const node = schemaNode || schemaComment;
		if (node) {
			if (decOptions.pos[0] / this.scale !== node.x || decOptions.pos[1] / this.scale !== node.y) {
				_.merge(node, {
					x: decOptions.pos[0] / this.scale,
					y: decOptions.pos[1] / this.scale,
				});
			}
			this.$timeout(() => {
				this.modified = true;
			});
		}
	}

	/**
	 * @param {import('@knitiv/api-client-javascript').KNode} searchKid KID of the node to add
	 * @returns {Promise<any>}
	 */
	async nodeOpen(searchKid) {
		const x = 100;
		const y = 100;

		const node = await this.NodeService.load(searchKid);
		if (node?.constructor === Node) {
			const id = this.User.getRef();
			const schemaNode = new SchemaNode(node);
			await this.$timeout(async () => {
				schemaNode.id = id;
				schemaNode.existK = true;
				schemaNode.shape = node.objtype === 'CLASS' ? 'RECTANGLE' : 'ELLIPSIS';
				schemaNode.node.objtype = node.objtype;
				schemaNode.x = Math.round((x / this.scale) / GRID) * GRID;
				schemaNode.y = Math.round((y / this.scale) / GRID) * GRID;
				schemaNode.show();
				await this.nodeEdit(schemaNode, {
					create: true,
					isaNodeCreate: true,
					tab: 'info',
				});
			});
		}
	}

	/**
	 * @param {SchemaNode} schemaNode
	 */
	async nodeUpdate(schemaNode) {
		if (this.selected.length === 0 || !this.selected[0].node) {
			return;
		}

		const currentSchemaNode = this.selected[0].node;
		if (currentSchemaNode instanceof SchemaNode) {
			currentSchemaNode.shape = schemaNode.shape;
			currentSchemaNode.readOnly = schemaNode.readOnly;
			currentSchemaNode.existK = schemaNode.existK;
			const oldNodeKid = currentSchemaNode.node.kid;
			_.extend(currentSchemaNode.node, schemaNode.node);

			// update node on all nodes
			for (const schemaNode2 of this.schema?.schemaNodes || []) {
				// #667 update node kid in dico in all nodes
				if (oldNodeKid !== currentSchemaNode.node.kid) {
					for (const [k, dico] of Object.entries(schemaNode2.node.kndef?.CLASS?.DICO || {})) {
						if (dico.objnum2_kid === oldNodeKid) {
							schemaNode2.node.kndef.CLASS.DICO[k].objnum2_kid = currentSchemaNode.node.kid;
						}

						if (dico.linker_kid === oldNodeKid) {
							schemaNode2.node.kndef.CLASS.DICO[k].linker_kid = currentSchemaNode.node.kid;
						}
					}
				}

				if (schemaNode2.node.kid === schemaNode.node.kid) {
					schemaNode2.node = currentSchemaNode.node;
				}
			}

			// update links from kndef
			// for (const [k, dico] of Object.entries(node.node.kndef.CLASS.DICO)) {
			// 	for (const common of node.node.kndef.CLASS.COMMON) {
			// 		if (common.dico_id === k) {
			// 			for (const link of this.schema.links) {
			// 				if (link.dico_id === k && dico.objnum2_kid === link.to.kid && dico.linker_kid === link.linker.kid) {
			// 					link.type = dico.relation_kid;
			// 					link.from.card = common.card_from === Defaults.KCARDN ? 'N' : common.card_from;
			// 					link.from.to = common.card_to === Defaults.KCARDN ? 'N' : common.card_to;
			// 				}
			// 			}
			// 		}
			// 	}
			// }

			// delete links from kndef
			const linksToDelete = _.remove(
				this.schema?.links || [],
				link => link.from.id === schemaNode.id && !(
					!link.dico_id || link.dico_id in schemaNode.node.kndef.CLASS.DICO
				),
			);

			if (this.selected[0].options?.create) {
				// automatically add attached nodes
				if (this.selected[0].options.autoNodes) {
					// eslint-disable-next-line promise/no-nesting
					await this.autoAddNodes(currentSchemaNode);
					this.setDraggable();
					await this.autoAddLinks(currentSchemaNode);
					this.addLinkIsa(currentSchemaNode);
				}
				// automatically add link between existing nodes
				else if (this.selected[0].options.autoLinks) {
					await this.autoAddLinks(currentSchemaNode);
					this.addLinkIsa(currentSchemaNode);
				}
			}

			this.$timeout(() => {
				for (const link of linksToDelete) {
					// @ts-ignore
					this.jsp?.deleteConnection(link.getConnection(), { force: true });
				}

				if (this.selected.length > 0) {
					this.unselectAll();
				}
			});

			this.modified = true;
		}
	}

	/**
	 * Saves the schema
	 *
	 * @returns {Promise<any>} Promise result
	 */
	async save() {
		if (this.schema) {
			this.schema.kjson = this.schema.export();

			const schema = _.cloneDeep(this.schema);
			schema.schemaNodes.length = 0;
			schema.kid = `K_NODE;${this.id}`;
			/**
			 * @param {any} result
			 */
			if (await this.NodeService.update(schema)) {
				this.create = false;
				this.modified = false;
				return true;
			}
		}
		return false;
	}

	/**
	 * Display the schema rights for editing
	 *
	 * @returns {Promise<any>} Promise result
	 */
	async schemaEditDroits() {
		const newSchema = await this.$uibModal.open({
			ariaLabelledBy:  'modal-droit-title',
			ariaDescribedBy: 'modal-droit-body',
			template:        templateModalDroit,
			controller:      ModalDroitController,
			controllerAs:    '$ctrl',
			resolve:         {
				node: () => this.schema,
				edit: () => this.edit,
			},
		}).result;
		_.extend(this.schema?.droit, newSchema.droit);
		this.modified = true;
	}

	/**
	 * @param {SchemaNode | SchemaLink | SchemaComment} node
	 * @param {any} options
	 */
	async select(node, options) {
		// Deselect previously selected node
		const done = await this.checkSelected(options);

		if (!options.multiple && this.selected.length > 0) {
			this.unselectAll();
		}

		if (done) {
			node.selected = true;
		}

		if (node instanceof SchemaLink) {
			const connection = node.getConnection();
			if (connection && done && node) {
				// @ts-ignore
				$(connection.getOverlay('label').canvas).addClass('selected');
			}
		}
		else if (options.multiple) {
			if (this.selected.length >= 0 && this.#lastSelectedElement) {
				this.jsp?.addToPosse(this.#lastSelectedElement, 'posse');
				this.#lastSelectedElement = null;
			}
			this.jsp?.addToPosse(`kn-${options.type}-${this.id}-${node.id}`, 'posse');
		}
		else {
			this.#lastSelectedElement = `kn-${options.type}-${this.id}-${node.id}`;
		}
		this.selected.push({
			modified: false,
			node,
			options,
		});
	}

	setDraggable() {
		const $schemaContainer = $('.tab-pane:visible').find('.schema-content');
		const $schemaEditor = $schemaContainer.find('.schema-editor');

		// suspend drawing and initialise.
		const $nodeList = $schemaEditor.find('.node').not('.jtk-managed');

		// mettre à jour la position x/y en cas de déplacement
		this.jsp?.draggable($nodeList, {
			// @ts-ignore
			containment:   $schemaEditor,
			stop:          (/** @type {any} */ decOptions) => this.nodeMoveStop(decOptions),
			disabled:      !this.edit,
			snapThreshold: 13,
			grid:          [
				GRID,
				GRID,
			],
		});
		this.jsp?.setDraggable($nodeList, this.edit);

		return $nodeList;
	}

	/**
	 */
	unselectAll() {
		this.jsp?.removeFromAllPosses(
			// @ts-ignore
			this.selected.filter(s => !(s.node instanceof SchemaLink)).map(s => `kn-node-${this.id}-${s.node.id}`),
		);
		for (const n of this.selected) {
			if (n.node instanceof SchemaLink) {
				const connection = n.node.getConnection();
				if (connection) {
					// @ts-ignore
					$(connection.getOverlay('label').canvas).removeClass('selected');
				}
			}
			n.node.selected = false;
		}
		this.selected.length = 0;
	}
}
